import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { AlertCircle, CheckCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';

export type ToastType = 'error' | 'success' | 'info';

export interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
}

interface ToastContainerProps {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}

/** 確定按鈕隱藏時機（毫秒） */
export const TOAST_BUTTON_HIDE_MS = 500;
/** 整則通知自動關閉時機（毫秒） */
export const TOAST_AUTO_DISMISS_MS = 800;

const typeConfig: Record<
  ToastType,
  { icon: React.ElementType; accentClass: string; title: string }
> = {
  error: {
    icon: AlertCircle,
    accentClass: 'text-destructive bg-destructive/15 border-destructive/40',
    title: '錯誤',
  },
  success: {
    icon: CheckCircle,
    accentClass: 'text-secondary bg-secondary/15 border-secondary/40',
    title: '成功',
  },
  info: {
    icon: Info,
    accentClass: 'text-primary bg-primary/15 border-primary/40',
    title: '提示',
  },
};

interface ToastDialogProps {
  toast: ToastItem;
  onDismiss: (id: number) => void;
}

/**
 * 功能：單則視窗化通知（可按確定關閉，或依時間自動消失）
 * @param {ToastDialogProps} props 通知資料與關閉回呼
 * @returns {JSX.Element} 置中對話框
 */
function ToastDialog({ toast, onDismiss }: ToastDialogProps) {
  const [showConfirm, setShowConfirm] = useState(true);
  const { icon: Icon, accentClass, title } = typeConfig[toast.type];

  useEffect(() => {
    const hideBtnTimer = window.setTimeout(() => setShowConfirm(false), TOAST_BUTTON_HIDE_MS);
    const dismissTimer = window.setTimeout(() => onDismiss(toast.id), TOAST_AUTO_DISMISS_MS);
    return () => {
      window.clearTimeout(hideBtnTimer);
      window.clearTimeout(dismissTimer);
    };
  }, [toast.id, onDismiss]);

  return (
    <motion.div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={`toast-title-${toast.id}`}
      aria-describedby={`toast-desc-${toast.id}`}
      initial={{ opacity: 0, scale: 0.92, y: 8 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96, y: 4 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      className="relative w-full max-w-sm rounded-3xl border border-border/80 bg-muted/95 p-6 shadow-2xl backdrop-blur-xl"
    >
      <div className="flex flex-col items-center text-center gap-4">
        <div
          className={`w-14 h-14 rounded-2xl border flex items-center justify-center ${accentClass}`}
        >
          <Icon size={28} />
        </div>
        <div className="space-y-2">
          <h2 id={`toast-title-${toast.id}`} className="text-lg font-black tracking-tight">
            {title}
          </h2>
          <p
            id={`toast-desc-${toast.id}`}
            className="text-sm font-medium text-muted-foreground leading-relaxed whitespace-pre-wrap"
          >
            {toast.message}
          </p>
        </div>

        <AnimatePresence initial={false}>
          {showConfirm && (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.15 }}
              className="w-full overflow-hidden"
            >
              <Button
                type="button"
                className="w-full h-11 rounded-xl font-bold"
                onClick={() => onDismiss(toast.id)}
              >
                確定
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

/**
 * 功能：以置中視窗方式顯示 Toast 佇列（同時間顯示最新一則）
 * @param {ToastContainerProps} props toasts 與 dismiss 回呼
 * @returns {JSX.Element | null} 遮罩與對話框
 */
export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  const active = toasts[toasts.length - 1];

  return (
    <AnimatePresence>
      {active ? (
        <motion.div
          key="toast-overlay"
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <div
            className="absolute inset-0 bg-black/55 backdrop-blur-[2px]"
            aria-hidden="true"
            onClick={() => onDismiss(active.id)}
          />
          <div className="relative z-10 w-full max-w-sm">
            <AnimatePresence mode="wait">
              <ToastDialog key={active.id} toast={active} onDismiss={onDismiss} />
            </AnimatePresence>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
